<script setup lang="ts">
import { computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength, email as emailRule, helpers } from '@vuelidate/validators'

const msg = (message: string, validator: any) => helpers.withMessage(message, validator)

interface FormData {
  title: string
  slug: string
  street: string
  street_number: string
  zip: string
  city: string
  areal: string
  locationgroup: string
  latitude: string
  longitude: string
  email: string
  telephone: string
  public: boolean
}

const props = defineProps<{
  modelValue: FormData
  errors: Record<string, string[]>
  mode: 'create' | 'edit'
}>()

const emit = defineEmits<{
  'update:modelValue': [value: FormData]
}>()

const form = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const update = (field: keyof FormData, value: any) => {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

const rules = computed(() => ({
  title: {
    required: msg('Titel ist ein Pflichtfeld.', required),
    maxLength: msg('Titel darf max. 255 Zeichen lang sein.', maxLength(255)),
  },
  street: {
    required: msg('Straße ist ein Pflichtfeld.', required),
    maxLength: msg('Straße darf max. 255 Zeichen lang sein.', maxLength(255)),
  },
  street_number: {
    required: msg('Hausnummer ist ein Pflichtfeld.', required),
    maxLength: msg('Hausnummer darf max. 255 Zeichen lang sein.', maxLength(255)),
  },
  zip: {
    required: msg('PLZ ist ein Pflichtfeld.', required),
    maxLength: msg('PLZ darf max. 255 Zeichen lang sein.', maxLength(255)),
  },
  city: {
    required: msg('Stadt ist ein Pflichtfeld.', required),
    maxLength: msg('Stadt darf max. 255 Zeichen lang sein.', maxLength(255)),
  },
  email: {
    emailRule: msg('Bitte eine gültige E-Mail-Adresse eingeben.', emailRule),
    maxLength: msg('E-Mail darf max. 255 Zeichen lang sein.', maxLength(255)),
  },
  telephone: { maxLength: msg('Telefon darf max. 255 Zeichen lang sein.', maxLength(255)) },
  locationgroup: { maxLength: msg('Gruppe darf max. 10 Zeichen lang sein.', maxLength(10)) },
  areal: { maxLength: msg('Areal darf max. 10 Zeichen lang sein.', maxLength(10)) },
  latitude: { maxLength: msg('Latitude darf max. 17 Zeichen lang sein.', maxLength(17)) },
  longitude: { maxLength: msg('Longitude darf max. 17 Zeichen lang sein.', maxLength(17)) },
}))

const v$ = useVuelidate(rules, form, { $lazy: true })

const getError = (field: string): string | null => {
  // Server errors take priority
  if (props.errors[field]?.length) {
    return props.errors[field][0]
  }
  // Then vuelidate errors
  const vField = (v$.value as any)[field]
  if (vField?.$errors?.length) {
    return vField.$errors[0].$message
  }
  return null
}

const hasError = (field: string): boolean => {
  return !!getError(field)
}

defineExpose({ v$ })
</script>

<template>
  <div>
    <h5 class="text-lg font-bold mb-4">Allgemeine Angaben</h5>
    <div class="flex flex-wrap">
      <label class="form-control w-full" :class="{ 'md:w-1/2 md:pr-2': mode === 'edit' }">
        <div class="label">
          <span class="label-text">Titel*</span>
        </div>
        <input type="text" :value="form.title" @input="update('title', ($event.target as HTMLInputElement).value)" @blur="v$.title.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('title') }" maxlength="255" />
        <div class="label pb-0" v-if="getError('title')"><span class="label-text-alt text-error">{{ getError('title') }}</span></div>
      </label>
      <label v-if="mode === 'edit'" class="form-control w-full md:w-1/2 md:pl-2">
        <div class="label">
          <span class="label-text">Slug*</span>
        </div>
        <input type="text" :value="form.slug" @input="update('slug', ($event.target as HTMLInputElement).value)" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('slug') }" maxlength="255" />
        <div class="label pb-0" v-if="getError('slug')"><span class="label-text-alt text-error">{{ getError('slug') }}</span></div>
      </label>
      <label class="form-control w-full md:w-1/2">
        <div class="label">
          <span class="label-text">Gruppe</span>
        </div>
        <input type="text" :value="form.locationgroup" @input="update('locationgroup', ($event.target as HTMLInputElement).value)" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('locationgroup') }" maxlength="10" />
      </label>
      <div class="form-control w-full">
        <label class="cursor-pointer label justify-start">
          <input type="checkbox" :checked="form.public" @change="update('public', ($event.target as HTMLInputElement).checked)" class="checkbox checkbox-info checkbox-sm" />
          <span class="label-text ml-2">Standort für Benutzer als Filteroption bereitstellen?</span>
        </label>
      </div>
    </div>

    <div class="divider"></div>

    <h5 class="text-lg font-bold mb-4">Kontakt</h5>
    <div class="flex flex-wrap">
      <div class="w-full md:w-1/2 md:pr-2">
        <label class="form-control">
          <div class="label">
            <span class="label-text">E-Mail-Addresse</span>
          </div>
          <input type="email" :value="form.email" @input="update('email', ($event.target as HTMLInputElement).value)" @blur="v$.email.$touch()" placeholder="name@example.com" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('email') }" maxlength="255" />
          <div class="label pb-0" v-if="getError('email')"><span class="label-text-alt text-error">{{ getError('email') }}</span></div>
        </label>
      </div>
      <div class="w-full md:w-1/2 md:pl-2">
        <label class="form-control">
          <div class="label">
            <span class="label-text">Telefon-Nr.</span>
          </div>
          <input type="text" :value="form.telephone" @input="update('telephone', ($event.target as HTMLInputElement).value)" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('telephone') }" maxlength="255" />
        </label>
      </div>
      <div class="w-full italic">
        <small>Wird als Kontakt auf der Detailseite von verknüpften Veranstaltungen angezeigt, falls kein Kontakt explizit bei den Veranstaltungen angegeben wird</small>
      </div>
    </div>

    <div class="divider"></div>

    <h5 class="text-lg font-bold mb-4">Angaben zum Standort</h5>
    <div class="flex flex-wrap md:flex-nowrap md:-mx-1">
      <div class="form-group w-full md:w-4/12 md:px-1">
        <label class="form-control">
          <div class="label"><span class="label-text">Straße*</span></div>
          <input type="text" :value="form.street" @input="update('street', ($event.target as HTMLInputElement).value)" @blur="v$.street.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('street') }" maxlength="255" />
        </label>
      </div>
      <div class="form-group w-full md:w-1/12 md:px-1">
        <label class="form-control">
          <div class="label"><span class="label-text">Nr.*</span></div>
          <input type="text" :value="form.street_number" @input="update('street_number', ($event.target as HTMLInputElement).value)" @blur="v$.street_number.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('street_number') }" maxlength="255" />
        </label>
      </div>
      <div class="form-group w-full md:w-2/12 md:px-1">
        <label class="form-control">
          <div class="label"><span class="label-text">PLZ*</span></div>
          <input type="text" :value="form.zip" @input="update('zip', ($event.target as HTMLInputElement).value)" @blur="v$.zip.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('zip') }" maxlength="255" />
        </label>
      </div>
      <div class="form-group w-full md:w-3/12 md:px-1">
        <label class="form-control">
          <div class="label"><span class="label-text">Stadt*</span></div>
          <input type="text" :value="form.city" @input="update('city', ($event.target as HTMLInputElement).value)" @blur="v$.city.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('city') }" maxlength="255" />
        </label>
      </div>
      <div class="form-group w-full md:w-2/12 md:px-1">
        <label class="form-control">
          <div class="label"><span class="label-text">Areal</span></div>
          <input type="text" :value="form.areal" @input="update('areal', ($event.target as HTMLInputElement).value)" class="input input-bordered input-sm w-full" maxlength="10" />
        </label>
      </div>
    </div>
    <div class="flex flex-wrap md:flex-nowrap md:-mx-1">
      <div class="form-group w-full md:w-6/12 md:px-1">
        <label class="form-control">
          <div class="label"><span class="label-text">Latitude</span></div>
          <input type="text" :value="form.latitude" @input="update('latitude', ($event.target as HTMLInputElement).value)" placeholder="51.489073" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('latitude') }" maxlength="17" />
        </label>
      </div>
      <div class="form-group w-full md:w-6/12 md:px-1">
        <label class="form-control">
          <div class="label"><span class="label-text">Longitude</span></div>
          <input type="text" :value="form.longitude" @input="update('longitude', ($event.target as HTMLInputElement).value)" placeholder="7.043123" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('longitude') }" maxlength="17" />
        </label>
      </div>
    </div>
  </div>
</template>
